/**
 * <s-clothing-rail> — a clothing rack with a focus that moves along the garments,
 * and a detail view per garment.
 *
 * Rack view ([data-rail-rack]):
 *   Pointing at a garment with a mouse, clicking/tapping it (--select), --rack-prev/--rack-next, scrolling over the rack
 *   (when data-scroll-selects="true"), swiping on
 *   narrow screens, or Left/Right/Home/End keys move the focus; the focused [data-rail-item]
 *   gets data-active and neighbours get data-distance (CSS turns it to face forward). Clicking the
 *   selected garment, or --open-active, opens its detail view. [data-rail-name], [data-rail-price]
 *   and [data-rail-link] show the selected item's data-title, data-price and data-url;
 *   [data-rail-progress-bar] slides to the selected position; each item gets --sag-y so its hook meets
 *   the curved [data-rail-pole] (curve depth from the --rail-sag custom property);
 *   [data-rail-prev]/[data-rail-next] disable at the ends.
 *
 * Detail views ([data-rail-detail], one per item, same order):
 *   Commands (buttons with commandfor="<rail id>"):
 *     --open    open the detail view of the item containing the source button
 *     --close   back to the rack (Escape also closes)
 *     --prev / --next   previous / next item's detail view (wraps)
 *     --colour  show the colour in source[data-rail-colour]
 *     --side    show source[data-rail-side] ("front" | "back")
 *   Views: [data-rail-view="<colour index>"][data-side="front|back"].
 *
 * Quick add ([data-rail-quick-add] = <s-product-form> inside a dialog):
 *   Radios [data-rail-option="<option index>"] resolve the variant from the
 *   [data-rail-variants] JSON, updating input[name="id"], [data-rail-qa-price] and
 *   [data-rail-qa-submit]. The dialog closes after a successful add.
 *
 * data-animate="false" disables smooth scrolling; reduced motion is respected.
 */
class SClothingRail extends HTMLElement {
  #ac;
  #observers = [];
  #items = [];
  #details = [];
  #index = 0;
  #open = -1;
  #settleTimer = 0;
  #wheelAcc = 0;
  #wheelLock = 0;
  #sagFrame = 0;
  #sagUntil = 0;
  #resizeObserver;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    this.rack = this.querySelector('[data-rail-rack]');
    this.track = this.querySelector('[data-rail-track]');
    this.nameEl = this.querySelector('[data-rail-name]');
    this.priceEl = this.querySelector('[data-rail-price]');
    this.linkEl = this.querySelector('[data-rail-link]');
    this.prevBtn = this.querySelector('[data-rail-prev]');
    this.nextBtn = this.querySelector('[data-rail-next]');
    this.pole = this.querySelector('[data-rail-pole]');
    this.progressBar = this.querySelector('[data-rail-progress-bar]');
    this.#items = [...this.querySelectorAll('[data-rail-item]')];
    this.#details = [...this.querySelectorAll('[data-rail-detail]')];
    if (!this.track || this.#items.length === 0) return;

    this.addEventListener('command', this.#handleCommand, opts);
    this.addEventListener('keydown', this.#handleDetailKeydown, opts);
    this.addEventListener('change', this.#handleChange, opts);
    this.track.addEventListener('keydown', this.#handleRackKeydown, opts);
    this.track.addEventListener('wheel', this.#handleWheel, { ...opts, passive: false });
    this.track.addEventListener('scroll', this.#handleScroll, { ...opts, passive: true });
    this.track.addEventListener('scroll', this.#queueSag, { ...opts, passive: true });
    this.track.addEventListener('pointerover', this.#handlePointerOver, opts);
    this.#resizeObserver = new ResizeObserver(this.#queueSag);
    this.#resizeObserver.observe(this.track);

    this.querySelectorAll('[data-rail-quick-add]').forEach((form) => this.#watchQuickAdd(form));
    this.#render();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#observers.forEach((observer) => observer.disconnect());
    this.#observers = [];
    clearTimeout(this.#settleTimer);
    cancelAnimationFrame(this.#sagFrame);
    this.#resizeObserver?.disconnect();
  }

  get #smooth() {
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    return !reduce && this.dataset.animate !== 'false';
  }

  get #isWide() {
    return window.matchMedia('(min-width: 768px)').matches;
  }

  get #rtl() {
    return getComputedStyle(this).direction === 'rtl';
  }

  /* ---------- Commands ---------- */

  #handleCommand = (event) => {
    const source = event.source instanceof HTMLElement ? event.source : null;
    const detail = source?.closest('[data-rail-detail]');
    switch (event.command) {
      case '--select': {
        // First click brings a garment forward; clicking the forward garment opens its details.
        const index = this.#items.indexOf(source?.closest('[data-rail-item]'));
        if (index < 0) break;
        if (index === this.#index) this.#openDetail(index);
        else this.#select(index, { scroll: true });
        break;
      }
      case '--open': {
        const index = this.#items.indexOf(source?.closest('[data-rail-item]'));
        if (index > -1) this.#openDetail(index);
        break;
      }
      case '--open-active':
        this.#openDetail(this.#index);
        break;
      case '--rack-prev':
        this.#select(this.#index - 1, { scroll: true });
        break;
      case '--rack-next':
        this.#select(this.#index + 1, { scroll: true });
        break;
      case '--close':
        this.#closeDetail();
        break;
      case '--prev':
        this.#openDetail(this.#wrap(this.#open - 1), { keepScroll: true });
        break;
      case '--next':
        this.#openDetail(this.#wrap(this.#open + 1), { keepScroll: true });
        break;
      case '--colour':
        if (detail) this.#setColour(detail, Number(source.dataset.railColour), { syncForm: true });
        break;
      case '--side':
        if (detail) this.#setSide(detail, source.dataset.railSide);
        break;
    }
  };

  #wrap(index) {
    const count = this.#items.length;
    return ((index % count) + count) % count;
  }

  /* ---------- Rack ---------- */

  #select(index, { scroll = false } = {}) {
    const next = Math.max(0, Math.min(this.#items.length - 1, index));
    if (next === this.#index) return;
    this.#index = next;
    this.#render();
    if (scroll && !this.#isWide) this.#scrollToIndex(next);
  }

  #render() {
    this.#items.forEach((item, i) => {
      const distance = Math.min(Math.abs(i - this.#index), 3);
      const active = distance === 0;
      item.toggleAttribute('data-active', active);
      item.dataset.distance = String(distance);
      const button = item.querySelector('button');
      if (button) {
        button.tabIndex = active ? 0 : -1;
        button.setAttribute('aria-pressed', String(active));
      }
    });
    const item = this.#items[this.#index];
    if (item) {
      const { title = '', price = '', url = '' } = item.dataset;
      if (this.nameEl) this.nameEl.textContent = title;
      if (this.priceEl) this.priceEl.textContent = price;
      if (this.linkEl && url) this.linkEl.href = url;
    }
    if (this.prevBtn) this.prevBtn.disabled = this.#index <= 0;
    if (this.nextBtn) this.nextBtn.disabled = this.#index >= this.#items.length - 1;
    if (this.progressBar) {
      const step = this.#rtl ? -100 : 100;
      this.progressBar.style.transform = `translateX(${this.#index * step}%)`;
    }
    // Follow the garments while their widths animate so every hook stays on the curved rail.
    this.#sagUntil = performance.now() + 750;
    this.#queueSag();
  }

  /* ---------- Hover: the garment under a mouse pointer turns to face forward straight away ---------- */

  #handlePointerOver = (event) => {
    if (event.pointerType !== 'mouse') return;
    const item = event.target instanceof Element ? event.target.closest('[data-rail-item]') : null;
    const index = this.#items.indexOf(item);
    if (index > -1 && index !== this.#index) this.#select(index);
  };

  /* ---------- Curved rail: drop each garment so its hook meets the sagging pole ---------- */

  #queueSag = () => {
    cancelAnimationFrame(this.#sagFrame);
    this.#sagFrame = requestAnimationFrame(this.#layoutSag);
  };

  #layoutSag = () => {
    const sag = parseFloat(getComputedStyle(this).getPropertyValue('--rail-sag')) || 0;
    const pole = this.pole?.getBoundingClientRect();
    if (pole && pole.width > 0) {
      this.#items.forEach((item) => {
        const rect = item.getBoundingClientRect();
        const u = Math.min(1, Math.max(0, (rect.left + rect.width / 2 - pole.left) / pole.width));
        item.style.setProperty('--sag-y', `${(4 * sag * u * (1 - u)).toFixed(2)}px`);
      });
    }
    if (performance.now() < this.#sagUntil) this.#sagFrame = requestAnimationFrame(this.#layoutSag);
  };

  #scrollToIndex(index) {
    const item = this.#items[index];
    if (!item) return;
    const trackRect = this.track.getBoundingClientRect();
    const itemRect = item.getBoundingClientRect();
    const offset = itemRect.left + itemRect.width / 2 - (trackRect.left + trackRect.width / 2);
    this.track.scrollBy({ left: offset, behavior: this.#smooth ? 'smooth' : 'auto' });
  }

  #handleWheel = (event) => {
    if (this.dataset.scrollSelects !== 'true' || !this.#isWide) return;
    const delta = Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY;
    if (delta === 0) return;
    const forward = delta > 0;
    // At either end, let the page keep scrolling.
    if ((forward && this.#index >= this.#items.length - 1) || (!forward && this.#index <= 0)) {
      this.#wheelAcc = 0;
      return;
    }
    event.preventDefault();
    const now = performance.now();
    if (now < this.#wheelLock) return;
    this.#wheelAcc += delta;
    if (Math.abs(this.#wheelAcc) >= 40) {
      this.#select(this.#index + (this.#wheelAcc > 0 ? 1 : -1));
      this.#wheelAcc = 0;
      this.#wheelLock = now + 90;
    }
  };

  #handleScroll = () => {
    if (this.#isWide) return;
    clearTimeout(this.#settleTimer);
    this.#settleTimer = setTimeout(() => {
      const trackRect = this.track.getBoundingClientRect();
      const centre = trackRect.left + trackRect.width / 2;
      let nearest = this.#index;
      let best = Infinity;
      this.#items.forEach((item, i) => {
        const rect = item.getBoundingClientRect();
        const gap = Math.abs(rect.left + rect.width / 2 - centre);
        if (gap < best) {
          best = gap;
          nearest = i;
        }
      });
      this.#select(nearest);
    }, 120);
  };

  #handleRackKeydown = (event) => {
    const keys = { ArrowRight: 1, ArrowLeft: -1 };
    let step = keys[event.key];
    let target;
    if (step !== undefined) {
      if (this.#rtl) step = -step;
      target = this.#index + step;
    } else if (event.key === 'Home') {
      target = 0;
    } else if (event.key === 'End') {
      target = this.#items.length - 1;
    } else {
      return;
    }
    event.preventDefault();
    this.#select(target, { scroll: true });
    this.#items[this.#index]?.querySelector('button')?.focus({ preventScroll: true });
  };

  /* ---------- Detail views ---------- */

  #openDetail(index, { keepScroll = false } = {}) {
    const detail = this.#details[index];
    if (!detail) return;
    if (this.rack) this.rack.hidden = true;
    this.#details.forEach((d, i) => (d.hidden = i !== index));
    this.#open = index;
    this.#index = index;
    this.#render();

    if (!keepScroll) {
      const top = this.getBoundingClientRect().top;
      if (top < 0 || top > window.innerHeight * 0.5) {
        this.scrollIntoView({ block: 'start', behavior: this.#smooth ? 'smooth' : 'auto' });
      }
    }
    detail.querySelector('h3[tabindex]')?.focus({ preventScroll: true });
  }

  #closeDetail() {
    if (this.#open < 0) return;
    this.#details.forEach((d) => (d.hidden = true));
    if (this.rack) this.rack.hidden = false;
    this.#open = -1;
    this.#render();
    requestAnimationFrame(() => {
      if (!this.#isWide) this.#scrollToIndex(this.#index);
      this.#items[this.#index]?.querySelector('button')?.focus({ preventScroll: true });
    });
  }

  #handleDetailKeydown = (event) => {
    if (event.key !== 'Escape' || this.#open < 0) return;
    if (event.target.closest?.('dialog')) return;
    event.preventDefault();
    this.#closeDetail();
  };

  #state(detail) {
    return {
      colour: Number(detail.dataset.colour || 0),
      side: detail.dataset.side || 'front',
    };
  }

  #hasView(detail, colour, side) {
    return Boolean(detail.querySelector(`[data-rail-view="${colour}"][data-side="${side}"]`));
  }

  #showView(detail) {
    const { colour, side } = this.#state(detail);
    detail.querySelectorAll('[data-rail-view]').forEach((view) => {
      view.hidden = !(Number(view.dataset.railView) === colour && view.dataset.side === side);
    });
    const hasBack = this.#hasView(detail, colour, 'back');
    detail.querySelectorAll('[data-rail-side]').forEach((button) => {
      button.setAttribute('aria-pressed', String(button.dataset.railSide === side));
      if (button.dataset.railSide === 'back') button.disabled = !hasBack;
    });
  }

  #setColour(detail, colour, { syncForm = false } = {}) {
    if (!Number.isFinite(colour)) return;
    detail.dataset.colour = String(colour);
    if (!this.#hasView(detail, colour, this.#state(detail).side)) detail.dataset.side = 'front';
    let value = '';
    detail.querySelectorAll('[data-rail-colour]').forEach((button) => {
      const pressed = Number(button.dataset.railColour) === colour;
      button.setAttribute('aria-pressed', String(pressed));
      if (pressed) value = button.dataset.value || '';
    });
    this.#showView(detail);

    if (syncForm && value) {
      const form = detail.querySelector('[data-rail-quick-add] form');
      const radio = [...(form?.querySelectorAll('input[data-rail-option]') || [])].find((input) => input.value === value);
      if (radio && !radio.checked) {
        radio.checked = true;
        this.#updateVariant(form);
      }
    }
  }

  #setSide(detail, side) {
    if (side !== 'front' && side !== 'back') return;
    const { colour } = this.#state(detail);
    if (!this.#hasView(detail, colour, side)) return;
    detail.dataset.side = side;
    this.#showView(detail);
  }

  /* ---------- Quick add ---------- */

  #handleChange = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.matches('[data-rail-option]')) return;
    const form = input.form;
    if (!form) return;
    this.#updateVariant(form);

    // Keep the big garment in step with the colour picked in the dialog.
    const detail = input.closest('[data-rail-detail]');
    const colourButton = [...(detail?.querySelectorAll('[data-rail-colour]') || [])].find(
      (button) => button.dataset.value === input.value
    );
    if (detail && colourButton) this.#setColour(detail, Number(colourButton.dataset.railColour));
  };

  #variants(form) {
    const holder = form.closest('[data-rail-quick-add]');
    if (!holder) return [];
    if (!holder._railVariants) {
      try {
        holder._railVariants = JSON.parse(holder.querySelector('[data-rail-variants]')?.textContent || '[]');
      } catch {
        holder._railVariants = [];
      }
    }
    return holder._railVariants;
  }

  #updateVariant(form) {
    const selected = [];
    form.querySelectorAll('input[data-rail-option]:checked').forEach((input) => {
      selected[Number(input.dataset.railOption)] = input.value;
    });
    const variant = this.#variants(form).find((v) => v.options.every((value, i) => selected[i] === undefined || selected[i] === value));
    const idInput = form.querySelector('input[name="id"]');
    const submit = form.querySelector('[data-rail-qa-submit]');
    const price = (form.closest('dialog') || form.closest('[data-rail-detail]'))?.querySelector('[data-rail-qa-price]');
    const available = Boolean(variant?.available);

    if (variant && idInput) idInput.value = String(variant.id);
    if (variant && price) price.textContent = variant.price;
    if (submit) {
      submit.disabled = !available;
      submit.textContent = available ? submit.dataset.labelAdd : submit.dataset.labelSoldOut;
    }
  }

  #watchQuickAdd(holder) {
    const observer = new MutationObserver(() => {
      if (holder.getAttribute('aria-busy') === 'true') return;
      const error = holder.querySelector('[data-add-to-cart-error]');
      if (error && !error.hidden) return;
      holder.closest('dialog')?.close();
    });
    observer.observe(holder, { attributes: true, attributeFilter: ['aria-busy'] });
    this.#observers.push(observer);
  }
}

if (!customElements.get('s-clothing-rail')) {
  customElements.define('s-clothing-rail', SClothingRail);
}
